// The widget stylesheet, injected into the Shadow DOM so the host page's CSS can't
// touch it and vice-versa. On the locked MarginArc brand: navy ink + a single vivid
// blue accent, Space Grotesk headlines / Inter body, flat surfaces, hairline borders,
// generous whitespace, no glows/gradients. Light is primary; dark via prefers-color-scheme.

export const STYLES = `
:host {
  --ink: #0E1F33;
  --steel: #1B3A5B;
  --accent: #1A73E8;
  --accent-deep: #1750A8;
  --text: #14283F;
  --text-mid: #45566E;
  --text-mute: #586B83;
  --page: #FFFFFF;
  --surface: #F4F6F9;
  --sunken: #E9EDF3;
  --border: #E2E7EE;
  --accent-tint: #E4EEFC;
  --r-sm: 6px;
  --r: 12px;
  --r-lg: 16px;
  --r-pill: 999px;
  --font-display: "Space Grotesk", system-ui, sans-serif;
  --font-body: "Inter", system-ui, sans-serif;
  --shadow: 0 10px 40px -16px rgba(14,31,51,.34);
  all: initial;
}
* { box-sizing: border-box; }

.launcher {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483000;
  display: inline-flex; align-items: center; gap: 10px;
  background: var(--ink); color: #fff; border: 0;
  border-radius: var(--r-pill); padding: 13px 20px 13px 18px;
  font-family: var(--font-body); font-weight: 600; font-size: 15px; line-height: 1;
  cursor: pointer; box-shadow: var(--shadow);
  transition: transform .15s ease, background .15s ease;
}
.launcher:hover { background: var(--steel); transform: translateY(-1px); }
.launcher:focus-visible { outline: 3px solid var(--accent); outline-offset: 2px; }
.launcher img { height: 18px; width: auto; display: block; }
.launcher.hidden { display: none; }

.panel {
  position: fixed; right: 20px; bottom: 20px; z-index: 2147483000;
  width: 392px; max-width: calc(100vw - 32px);
  height: 620px; max-height: calc(100vh - 40px);
  display: flex; flex-direction: column;
  background: var(--page); color: var(--text);
  border: 1px solid var(--border); border-radius: var(--r-lg);
  box-shadow: var(--shadow); overflow: hidden;
  font-family: var(--font-body);
}
.panel[hidden] { display: none; }

.head {
  display: flex; align-items: center; gap: 10px;
  background: var(--ink); color: #fff; padding: 14px 16px; flex: none;
}
.head img { height: 20px; width: auto; }
.head .sub { font-family: var(--font-body); font-size: 13px; color: #AEBBCE; font-weight: 500; }
.head .spacer { flex: 1; }
.head .status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #AEBBCE; }
.head .dot { width: 7px; height: 7px; border-radius: 50%; background: #46C27D; }
.head .close {
  background: transparent; border: 0; color: #AEBBCE; cursor: pointer;
  width: 40px; height: 40px; border-radius: var(--r-sm); font-size: 20px; line-height: 1;
  display: grid; place-items: center;
}
.head .close:hover { background: rgba(255,255,255,.10); color: #fff; }
.head .close:focus-visible { outline: 2px solid var(--accent); }

.msgs {
  flex: 1; overflow-y: auto; padding: 18px 16px; display: flex; flex-direction: column; gap: 14px;
  scroll-behavior: smooth;
}
.row { display: flex; flex-direction: column; max-width: 86%; }
.row.user { align-self: flex-end; align-items: flex-end; }
.row.bot { align-self: flex-start; align-items: flex-start; }

.bubble {
  font-size: 16px; line-height: 1.62; border-radius: var(--r);
  padding: 11px 14px; white-space: normal; word-wrap: break-word;
}
.row.user .bubble { background: var(--accent-tint); color: var(--text); border-bottom-right-radius: var(--r-sm); }
.row.bot .bubble { background: var(--surface); color: var(--text); border: 1px solid var(--border); border-bottom-left-radius: var(--r-sm); }
.bubble strong { font-weight: 600; }
.bubble code { font-family: ui-monospace, monospace; font-size: 13px; background: var(--sunken); padding: 1px 5px; border-radius: 4px; }

.cites { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.cite {
  font-size: 12px; color: var(--accent-deep); text-decoration: none;
  background: var(--accent-tint); border: 1px solid var(--border);
  padding: 3px 9px; border-radius: var(--r-pill);
}
.cite:hover { background: #d8e6fb; }
.cite:focus-visible { outline: 2px solid var(--accent); }

.cards { display: flex; flex-direction: column; gap: 8px; margin-top: 10px; width: 100%; }
.card {
  display: inline-flex; align-items: center; justify-content: space-between; gap: 8px;
  font-family: var(--font-body); font-weight: 600; font-size: 14px; text-decoration: none;
  border-radius: var(--r-sm); padding: 11px 14px; cursor: pointer; border: 1px solid transparent;
}
.card.primary { background: var(--accent); color: #fff; }
.card.primary:hover { background: var(--accent-deep); }
.card.secondary, .card.ghost { background: var(--page); color: var(--accent-deep); border-color: var(--border); }
.card.secondary:hover, .card.ghost:hover { background: var(--surface); }
.card:focus-visible { outline: 3px solid var(--accent); outline-offset: 1px; }
.card .arrow { opacity: .7; }

.chips { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.chip {
  font-family: var(--font-body); font-size: 13.5px; font-weight: 500; color: var(--text);
  background: var(--page); border: 1px solid var(--border); border-radius: var(--r-pill);
  padding: 8px 14px; cursor: pointer; transition: background .12s ease, border-color .12s ease;
}
.chip:hover { background: var(--accent-tint); border-color: var(--accent); }
.chip:focus-visible { outline: 2px solid var(--accent); }

.capture { margin-top: 10px; background: var(--surface); border: 1px solid var(--border); border-radius: var(--r); padding: 13px 14px; }
.capture-prompt { font-size: 14.5px; line-height: 1.45; color: var(--text); margin: 0 0 10px; }
.capture-form { display: flex; gap: 8px; }
.capture-input {
  flex: 1; font-family: var(--font-body); font-size: 15px; color: var(--text);
  background: var(--page); border: 1px solid var(--border); border-radius: var(--r-sm);
  padding: 10px 12px; min-height: 44px;
}
.capture-input:focus { outline: none; border-color: var(--accent); }
.capture-hp { position: absolute; left: -9999px; width: 1px; height: 1px; opacity: 0; }
.capture-send {
  flex: none; font-family: var(--font-body); font-weight: 600; font-size: 14px; color: #fff;
  background: var(--accent); border: 0; border-radius: var(--r-sm); padding: 0 16px; min-height: 44px; cursor: pointer;
}
.capture-send:hover { background: var(--accent-deep); }
.capture-send:disabled { background: var(--sunken); color: var(--text-mute); cursor: default; }
.capture-send:focus-visible { outline: 3px solid var(--accent); outline-offset: 1px; }
.capture-status { font-size: 12.5px; color: var(--text-mute); margin: 8px 0 0; }

.typing { display: inline-flex; gap: 4px; padding: 12px 14px; }
.typing span { width: 7px; height: 7px; border-radius: 50%; background: var(--text-mute); animation: blink 1.2s infinite both; }
.typing span:nth-child(2) { animation-delay: .2s; }
.typing span:nth-child(3) { animation-delay: .4s; }
@keyframes blink { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }

.feedback { display: flex; gap: 4px; margin-top: 6px; }
.feedback button {
  background: transparent; border: 0; cursor: pointer; color: var(--text-mute);
  width: 26px; height: 26px; border-radius: var(--r-sm); font-size: 13px;
}
.feedback button:hover { background: var(--surface); color: var(--text); }
.feedback button[aria-pressed="true"] { color: var(--accent); }

.foot { border-top: 1px solid var(--border); padding: 12px; flex: none; background: var(--page); }
.form { display: flex; align-items: flex-end; gap: 8px; }
.form textarea {
  flex: 1; resize: none; font-family: var(--font-body); font-size: 15px; line-height: 1.4;
  color: var(--text); background: var(--page); border: 1px solid var(--border);
  border-radius: var(--r); padding: 11px 12px; max-height: 120px; min-height: 44px;
}
.form textarea:focus { outline: none; border-color: var(--accent); }
.form .send {
  flex: none; width: 44px; height: 44px; border: 0; border-radius: var(--r);
  background: var(--accent); color: #fff; cursor: pointer; display: grid; place-items: center;
}
.form .send:hover { background: var(--accent-deep); }
.form .send:disabled { background: var(--sunken); color: var(--text-mute); cursor: default; }
.form .send:focus-visible { outline: 3px solid var(--accent); outline-offset: 1px; }
.consent { margin-top: 8px; font-size: 11px; line-height: 1.4; color: var(--text-mute); }

@media (max-width: 480px) {
  .panel { right: 0; left: 0; bottom: 0; width: 100%; max-width: 100%; height: 90vh; max-height: 90vh;
    border-radius: var(--r-lg) var(--r-lg) 0 0; border-bottom: 0; }
  .launcher { right: 16px; bottom: 16px; }
  /* the title wordmark next to the logo is redundant with the logo and crowds the header on phones */
  .head .sub { display: none; }
}

@media (prefers-color-scheme: dark) {
  :host {
    --ink: #0B1320; --accent: #4D9FFF; --accent-deep: #3D86E6;
    --text: #E8EDF4; --text-mid: #AEBBCE; --text-mute: #8595AC;
    --page: #0B1320; --surface: #131C2B; --sunken: #0A111C; --border: #243349; --accent-tint: rgba(77,159,255,.12);
  }
  .card.primary { color: var(--ink); }
  .form .send { color: var(--ink); }
  .head { background: #060B14; }
}

@media (prefers-reduced-motion: reduce) {
  .launcher, .chip, .msgs { transition: none; scroll-behavior: auto; }
  .typing span { animation: none; }
}
`;
